import {
  LOCALES,
  MAX_FIT_SOURCE_BYTES,
  MAX_KML_SOURCE_BYTES,
  isLocale,
  messages,
  type ActivityChatResponse,
  type ActivityStoryNarrative,
  type Locale,
} from "@sporttrack/contracts";
import type {
  ActivityAnalysis,
  ActivityEvidenceCatalog,
  ActivityEvidenceFact,
} from "@sporttrack/domain";
import {
  lazy,
  Suspense,
  useEffect,
  useState,
  type CSSProperties,
  type FormEvent,
  type ReactNode,
} from "react";
import type { RoutePoint } from "./ActivityTerrain";

const ActivityTerrain = lazy(async () => {
  const module = await import("./ActivityTerrain");
  return { default: module.ActivityTerrain };
});

interface Envelope<T> {
  data: T;
  warnings: string[];
}
interface ActivitySummary {
  id: string;
  sport: string;
  title: string;
  localDate: string;
  distance: number | null;
  movingTime: number | null;
  ascent: number | null;
  version: number;
  warnings?: string[];
}

const bilingualWords = {
  en: {
    load: "Loading private evidence…",
    retry: "Reload",
    empty: "No countable activities yet.",
    import: "Import a FIT or KML Source Asset",
    sourceNotice:
      "The original is private. Missing observations stay unavailable; file names never enter public artifacts.",
    consentCheck: "Checking your first-import privacy decision…",
    firstImportTitle: "Before your first activity import",
    firstImportBody:
      "FIT and KML files can contain precise routes, timestamps, and sensor observations such as heart rate, cadence, power, and temperature.",
    firstImportBoundary:
      "SportTrack uses this data only to provide your owner-only activity analysis. Nothing becomes public unless you explicitly create a share. This processing is required for imports; the fictional demo remains available without it.",
    firstImportAcknowledgement:
      "I have reviewed this notice and want SportTrack to process my activity file for private analysis.",
    enableImports: "Enable private imports",
    consentSaved: "Private imports are enabled for this account.",
    upload: "Upload and analyze",
    home: "Current private period",
    activity: "Activity evidence",
    compare: "Eligible comparison",
    reports: "Immutable reports",
    settings: "Privacy and account controls",
    jobs: "Durable jobs",
    createShare: "Create fixed mapless share",
    export: "Request portable export",
    revokeSessions: "Sign out everywhere",
    deleteActivity: "Delete this Activity",
  },
  "zh-CN": {
    load: "正在加载私密证据…",
    retry: "重新加载",
    empty: "暂无可计数活动。",
    import: "导入 FIT 或 KML 源文件",
    sourceNotice: "原文件保持私密；缺失的观测数据仍为不可用，文件名不会出现在任何公开产物中。",
    consentCheck: "正在检查首次导入的隐私决定…",
    firstImportTitle: "首次导入活动文件前",
    firstImportBody:
      "FIT 和 KML 文件可能包含精确路线、时间戳，以及心率、步频、功率和温度等传感器观测数据。",
    firstImportBoundary:
      "SportTrack 仅使用这些数据提供仅你可见的活动分析。除非你明确创建分享，否则任何内容都不会公开。导入必须进行此项处理；即使不进行此处理，你仍可使用示例演示。",
    firstImportAcknowledgement: "我已阅读此说明，并希望 SportTrack 为私密分析处理我的活动文件。",
    enableImports: "启用私密导入",
    consentSaved: "此账户已启用私密导入。",
    upload: "上传并分析",
    home: "当前私密周期",
    activity: "活动证据",
    compare: "符合条件的比较",
    reports: "不可变报告",
    settings: "隐私与账户控制",
    jobs: "持久任务",
    createShare: "创建固定无地图分享",
    export: "申请可移植导出",
    revokeSessions: "退出所有设备",
    deleteActivity: "删除此活动",
  },
} as const;

type WorkspaceWords = { [K in keyof typeof bilingualWords.en]: string };
const workspaceWords = (overrides: Partial<WorkspaceWords>): WorkspaceWords => ({
  ...bilingualWords.en,
  ...overrides,
});
const words: Record<Locale, WorkspaceWords> = {
  en: bilingualWords.en,
  "zh-CN": bilingualWords["zh-CN"],
  fr: workspaceWords({
    load: "Chargement des données privées…",
    retry: "Recharger",
    empty: "Aucune activité comptabilisable.",
    import: "Importer un fichier FIT ou KML",
    upload: "Téléverser et analyser",
    home: "Période privée actuelle",
    activity: "Données d’activité",
    compare: "Comparaison admissible",
    reports: "Rapports immuables",
    settings: "Confidentialité et compte",
    jobs: "Tâches durables",
    createShare: "Créer un partage fixe sans carte",
    export: "Demander une exportation",
    revokeSessions: "Se déconnecter partout",
    deleteActivity: "Supprimer cette activité",
  }),
  de: workspaceWords({
    load: "Private Daten werden geladen…",
    retry: "Neu laden",
    empty: "Noch keine zählbaren Aktivitäten.",
    import: "FIT- oder KML-Datei importieren",
    upload: "Hochladen und analysieren",
    home: "Aktueller privater Zeitraum",
    activity: "Aktivitätsnachweise",
    compare: "Geeigneter Vergleich",
    reports: "Unveränderliche Berichte",
    settings: "Datenschutz und Konto",
    jobs: "Dauerhafte Aufgaben",
    createShare: "Fixierte Freigabe ohne Karte erstellen",
    export: "Export anfordern",
    revokeSessions: "Überall abmelden",
    deleteActivity: "Aktivität löschen",
  }),
  it: workspaceWords({
    load: "Caricamento delle prove private…",
    retry: "Ricarica",
    empty: "Nessuna attività conteggiabile.",
    import: "Importa un file FIT o KML",
    upload: "Carica e analizza",
    home: "Periodo privato attuale",
    activity: "Prove dell’attività",
    compare: "Confronto idoneo",
    reports: "Rapporti immutabili",
    settings: "Privacy e account",
    jobs: "Processi durevoli",
    createShare: "Crea condivisione fissa senza mappa",
    export: "Richiedi esportazione",
    revokeSessions: "Disconnetti ovunque",
    deleteActivity: "Elimina attività",
  }),
  nl: workspaceWords({
    load: "Privégegevens laden…",
    retry: "Opnieuw laden",
    empty: "Nog geen meetellende activiteiten.",
    import: "FIT- of KML-bestand importeren",
    upload: "Uploaden en analyseren",
    home: "Huidige privéperiode",
    activity: "Activiteitsbewijs",
    compare: "Geschikte vergelijking",
    reports: "Onveranderlijke rapporten",
    settings: "Privacy en account",
    jobs: "Duurzame taken",
    createShare: "Vaste deling zonder kaart maken",
    export: "Export aanvragen",
    revokeSessions: "Overal uitloggen",
    deleteActivity: "Activiteit verwijderen",
  }),
  es: workspaceWords({
    load: "Cargando datos privados…",
    retry: "Volver a cargar",
    empty: "Aún no hay actividades contabilizables.",
    import: "Importar un archivo FIT o KML",
    upload: "Subir y analizar",
    home: "Periodo privado actual",
    activity: "Datos de la actividad",
    compare: "Comparación apta",
    reports: "Informes inmutables",
    settings: "Privacidad y cuenta",
    jobs: "Tareas duraderas",
    createShare: "Crear compartición fija sin mapa",
    export: "Solicitar exportación",
    revokeSessions: "Cerrar sesión en todas partes",
    deleteActivity: "Eliminar actividad",
  }),
};

function errorText(locale: Locale, status: number): string {
  return locale !== "zh-CN"
    ? `The operation was not completed (safe code ${status}). Review the input or sign in again.`
    : `操作未完成（安全代码 ${status}），请检查输入或重新登录。`;
}

function statusFromError(error: unknown): number {
  const status = error instanceof Error ? Number(error.message) : 500;
  return Number.isInteger(status) && status >= 400 && status <= 599 ? status : 500;
}

async function api<T>(url: string, init?: RequestInit): Promise<{ value: T; response: Response }> {
  const response = await fetch(url, { credentials: "include", ...init });
  if (!response.ok) throw new Error(String(response.status));
  const payload: unknown = await response.json();
  const value =
    typeof payload === "object" && payload !== null && "data" in payload
      ? (payload as Envelope<T>).data
      : (payload as T);
  return { value, response };
}

function usePrivate<T>(url: string, refresh = 0) {
  const [state, setState] = useState<{ data: T | null; error: number | null }>({
    data: null,
    error: null,
  });
  useEffect(() => {
    const controller = new AbortController();
    setState({ data: null, error: null });
    api<T>(url, { signal: controller.signal })
      .then(({ value }) => setState({ data: value, error: null }))
      .catch((error: unknown) => {
        if (error instanceof DOMException) return;
        setState({ data: null, error: statusFromError(error) });
      });
    return () => controller.abort();
  }, [url, refresh]);
  return state;
}

function PanelState({ locale, error }: { locale: Locale; error: number | null }) {
  const t = words[locale];
  return error === null ? (
    <p className="private-loading" aria-live="polite">
      {t.load}
    </p>
  ) : (
    <div className="notice" role="alert">
      <p>{errorText(locale, error)}</p>
      <button onClick={() => location.reload()}>{t.retry}</button>
    </div>
  );
}

function ImportPanel({ locale, onComplete }: { locale: Locale; onComplete: () => void }) {
  const t = words[locale];
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const [consentState, setConsentState] = useState<
    "checking" | "required" | "saving" | "granted" | "error"
  >("checking");
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    api<{ decisions: { purpose: string; granted: boolean }[] }>("/v1/consents", {
      signal: controller.signal,
    })
      .then(({ value }) => {
        const latestCoreDecision = value.decisions.find((decision) => decision.purpose === "core");
        setConsentState(latestCoreDecision?.granted === true ? "granted" : "required");
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException) return;
        setConsentState("error");
        setStatus(errorText(locale, statusFromError(error)));
      });
    return () => controller.abort();
  }, [locale]);

  async function enableImports(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!acknowledged) return;
    setConsentState("saving");
    setStatus("");
    try {
      await api("/v1/consents", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          purpose: "core",
          granted: true,
          policyVersion: "privacy-v1",
          noticeVersion: "first-import-v1",
          locale,
        }),
      });
      setConsentState("granted");
      setStatus(t.consentSaved);
    } catch (error) {
      setConsentState("required");
      setStatus(errorText(locale, statusFromError(error)));
    }
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const file = new FormData(form).get("source");
    if (!(file instanceof File) || file.size === 0) return;
    const extension = file.name.split(".").at(-1)?.toLocaleLowerCase("en-US");
    if (extension !== "fit" && extension !== "kml") {
      setStatus(locale !== "zh-CN" ? "Choose a FIT or KML file." : "请选择 FIT 或 KML 文件。");
      return;
    }
    const maximumBytes = extension === "fit" ? MAX_FIT_SOURCE_BYTES : MAX_KML_SOURCE_BYTES;
    if (file.size > maximumBytes) {
      setStatus(
        locale !== "zh-CN"
          ? `This ${extension.toUpperCase()} file exceeds the measured ${extension === "fit" ? "256 KiB" : "20 MiB"} limit.`
          : `此 ${extension.toUpperCase()} 文件超过实测的${extension === "fit" ? " 256 KiB" : " 20 MiB"}限制。`,
      );
      return;
    }
    setBusy(true);
    setStatus(locale !== "zh-CN" ? "Allocating a private upload…" : "正在分配私密上传…");
    try {
      const created = await api<{
        id: string;
        upload: { url: string; headers: Record<string, string> };
      }>("/v1/imports", {
        method: "POST",
        headers: { "content-type": "application/json", "idempotency-key": crypto.randomUUID() },
        body: JSON.stringify({
          declaredType: extension,
          byteSize: file.size,
          displayName: file.name,
        }),
      });
      setStatus(
        locale !== "zh-CN"
          ? "Uploading directly to private object storage…"
          : "正在直接上传到私密对象存储…",
      );
      const uploaded = await fetch(created.value.upload.url, {
        method: "PUT",
        headers: created.value.upload.headers,
        body: file,
      });
      if (!uploaded.ok) throw new Error(String(uploaded.status));
      setStatus(
        locale !== "zh-CN"
          ? "Canonical analysis has started. You may leave this page."
          : "规范分析已开始，你可以离开此页面。",
      );
      await api(`/v1/imports/${created.value.id}/complete`, {
        method: "POST",
        headers: { "content-type": "application/json", "idempotency-key": crypto.randomUUID() },
        body: "{}",
      });
      setStatus(
        locale !== "zh-CN"
          ? "Import accepted; authoritative status is in Jobs."
          : "导入已受理，请以任务中的状态为准。",
      );
      form.reset();
      onComplete();
    } catch (error) {
      setStatus(errorText(locale, statusFromError(error)));
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="private-card import-panel">
      <p className="section-index">
        {locale !== "zh-CN" ? "PRIVATE / SOURCE ASSET" : "私密 / 源文件"}
      </p>
      <h2>{t.import}</h2>
      {consentState === "checking" ? (
        <p aria-live="polite">{t.consentCheck}</p>
      ) : consentState === "granted" ? (
        <>
          <p>{t.sourceNotice}</p>
          <form onSubmit={(event) => void submit(event)}>
            <input
              name="source"
              type="file"
              aria-label={locale !== "zh-CN" ? "FIT or KML source file" : "FIT 或 KML 源文件"}
              accept=".fit,.kml,application/vnd.ant.fit,application/vnd.google-earth.kml+xml"
              required
            />
            <button className="button" disabled={busy}>
              {busy ? "…" : t.upload}
            </button>
          </form>
        </>
      ) : consentState === "error" ? (
        <button className="button" onClick={() => location.reload()}>
          {t.retry}
        </button>
      ) : (
        <div className="first-import-notice">
          <h3>{t.firstImportTitle}</h3>
          <p>{t.firstImportBody}</p>
          <p>{t.firstImportBoundary}</p>
          <form onSubmit={(event) => void enableImports(event)}>
            <label className="consent-check">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(event) => setAcknowledged(event.currentTarget.checked)}
              />
              <span>{t.firstImportAcknowledgement}</span>
            </label>
            <button className="button" disabled={!acknowledged || consentState === "saving"}>
              {consentState === "saving" ? "…" : t.enableImports}
            </button>
          </form>
        </div>
      )}
      <p role="status">{status}</p>
    </section>
  );
}

function HomeView({ locale }: { locale: Locale }) {
  const [refresh, setRefresh] = useState(0);
  const home = usePrivate<{
    totals: { activityCount: number; distance: number; movingTime: number };
    recentActivities: ActivitySummary[];
    attention: Record<string, number>;
  }>("/v1/home", refresh);
  const jobs = usePrivate<{
    jobs: {
      id: string;
      jobType: string;
      state: string;
      progress: number;
      safeErrorCode: string | null;
    }[];
  }>("/v1/jobs", refresh);
  if (home.data === null) return <PanelState locale={locale} error={home.error} />;
  const t = words[locale];
  return (
    <>
      <header className="app-title">
        <div>
          <p className="eyebrow">
            {locale !== "zh-CN" ? "CURRENT PERIOD / OWNER ONLY" : "当前周期 / 仅本人可见"}
          </p>
          <h1>{t.home}</h1>
          <p>
            {locale !== "zh-CN"
              ? "Only countable, quality-eligible evidence contributes to these totals."
              : "只有可计数且质量合格的证据会计入这些汇总。"}
          </p>
        </div>
      </header>
      <section className="summary-grid">
        <Summary
          locale={locale}
          label={locale !== "zh-CN" ? "Activities" : "活动"}
          value={String(home.data.totals.activityCount)}
        />
        <Summary
          locale={locale}
          label={locale !== "zh-CN" ? "Distance" : "距离"}
          value={`${(home.data.totals.distance / 1000).toFixed(1)} km`}
        />
        <Summary
          locale={locale}
          label={locale !== "zh-CN" ? "Moving time" : "移动时间"}
          value={`${Math.round(home.data.totals.movingTime / 60)} min`}
        />
        <Summary
          locale={locale}
          label={locale !== "zh-CN" ? "Needs attention" : "需处理"}
          value={String(Object.values(home.data.attention).reduce((sum, value) => sum + value, 0))}
        />
      </section>
      <div className="private-layout">
        <ImportPanel locale={locale} onComplete={() => setRefresh((value) => value + 1)} />
        <section className="private-card">
          <p className="section-index">{t.jobs.toUpperCase()}</p>
          <h2>{t.jobs}</h2>
          {jobs.data === null ? (
            <PanelState locale={locale} error={jobs.error} />
          ) : jobs.data.jobs.length === 0 ? (
            <p>
              {locale !== "zh-CN" ? "No durable work needs attention." : "暂无需要处理的持久任务。"}
            </p>
          ) : (
            <ul className="evidence-list">
              {jobs.data.jobs.map((job) => (
                <li key={job.id}>
                  <strong>{job.jobType}</strong>
                  <span>
                    {job.state} · {job.progress}%
                    {job.safeErrorCode === null ? "" : ` · ${job.safeErrorCode}`}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
      <ActivityList locale={locale} activities={home.data.recentActivities} />
    </>
  );
}

function Summary({ locale, label, value }: { locale: Locale; label: string; value: string }) {
  return (
    <article className="summary-metric">
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{locale !== "zh-CN" ? "CANONICAL" : "规范"}</small>
    </article>
  );
}

function ActivityList({ locale, activities }: { locale: Locale; activities: ActivitySummary[] }) {
  if (activities.length === 0)
    return (
      <section className="private-card">
        <p>{words[locale].empty}</p>
      </section>
    );
  return (
    <section className="private-card">
      <p className="section-index">{locale !== "zh-CN" ? "CANONICAL HISTORY" : "规范活动历史"}</p>
      <div className="activity-table">
        {activities.map((activity) => (
          <a href={`/app/activities/${activity.id}`} key={activity.id}>
            <span>
              <strong>{activity.title}</strong>
              <small>
                {activity.sport} · {activity.localDate}
              </small>
            </span>
            <span>
              {activity.distance === null ? "N/A" : `${(activity.distance / 1000).toFixed(1)} km`}
            </span>
          </a>
        ))}
      </div>
    </section>
  );
}

function ActivitiesView({ locale }: { locale: Locale }) {
  const [query, setQuery] = useState("");
  const [refresh, setRefresh] = useState(0);
  const result = usePrivate<{ activities: ActivitySummary[] }>(
    `/v1/activities?search=${encodeURIComponent(query)}`,
    refresh,
  );
  return (
    <>
      <header className="app-title">
        <div>
          <p className="eyebrow">
            {locale !== "zh-CN" ? "SEARCHABLE / IMMUTABLE DATES" : "可搜索 / 日期不可变"}
          </p>
          <h1>{locale !== "zh-CN" ? "Activity history" : "活动历史"}</h1>
        </div>
      </header>
      <div className="private-layout">
        <ImportPanel locale={locale} onComplete={() => setRefresh((value) => value + 1)} />
        <section className="private-card">
          <label>
            <span>
              {locale !== "zh-CN"
                ? "Search titles, notes, equipment, places, and tags"
                : "搜索标题、备注、装备、地点和标签"}
            </span>
            <input value={query} onChange={(event) => setQuery(event.target.value)} type="search" />
          </label>
        </section>
      </div>
      {result.data === null ? (
        <PanelState locale={locale} error={result.error} />
      ) : (
        <ActivityList locale={locale} activities={result.data.activities} />
      )}
    </>
  );
}

interface ActivityMetric {
  key: string;
  value: number | null;
  canonicalUnit: string;
  state: string;
  provenance: string;
  coverage: number;
  confidence: string;
  warnings: string[];
}

interface ActivityStoryState {
  state: "absent" | "queued" | "generating" | "ready" | "failed";
  progress: number;
  safeErrorCode: string | null;
  story: {
    generatedAt: string;
    textModel: string;
    image: { state: "pending" | "ready" | "unavailable"; model: string };
    privacy: {
      coordinateFreeModelInput: true;
      exactRouteSharedWithModels: false;
      chatStoredBySportTrack: false;
    };
    evidenceCatalog: ActivityEvidenceCatalog;
    narrative: ActivityStoryNarrative;
  } | null;
  imageUrl: string | null;
}

function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(
    () => matchMedia("(prefers-reduced-motion: reduce)").matches,
  );
  useEffect(() => {
    const media = matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(media.matches);
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);
  return reduced;
}

function metricLabel(key: string, locale: Locale): string {
  const labels: Record<string, [string, string]> = {
    distance: ["Distance", "距离"],
    duration: ["Moving time", "移动时间"],
    movingTime: ["Moving time", "移动时间"],
    ascent: ["Ascent", "累计爬升"],
    averageSpeed: ["Average speed", "平均速度"],
    speed: ["Average speed", "平均速度"],
    elevationMaximum: ["Highest elevation", "最高海拔"],
    heartRate: ["Heart rate", "心率"],
    cadence: ["Cadence", "步频"],
    power: ["Power", "功率"],
    temperature: ["Temperature", "温度"],
    calories: ["Energy", "能量"],
  };
  return labels[key]?.[locale !== "zh-CN" ? 0 : 1] ?? key.replace(/([A-Z])/gu, " $1");
}

function formatMetric(metric: ActivityMetric | undefined, locale: Locale): string {
  if (metric === undefined || metric.value === null)
    return locale !== "zh-CN" ? "Not recorded" : "未记录";
  const value = metric.value;
  if (metric.key === "distance") return `${(value / 1000).toFixed(value >= 10_000 ? 1 : 2)} km`;
  if (metric.key === "duration" || metric.key === "movingTime") {
    const hours = Math.floor(value / 3600);
    const minutes = Math.floor((value % 3600) / 60);
    const seconds = Math.round(value % 60);
    return hours > 0
      ? `${hours}h ${String(minutes).padStart(2, "0")}m`
      : `${minutes}:${String(seconds).padStart(2, "0")}`;
  }
  if ((metric.key === "speed" || metric.key === "averageSpeed") && metric.canonicalUnit === "m/s")
    return `${(value * 3.6).toFixed(1)} km/h`;
  const digits = Math.abs(value) >= 100 ? 0 : 1;
  return `${value.toFixed(digits)} ${metric.canonicalUnit}`;
}

function evidenceFact(
  catalog: ActivityEvidenceCatalog | null,
  reference: string,
): ActivityEvidenceFact | null {
  return catalog?.facts.find((fact) => fact.id === reference) ?? null;
}

function formatSeconds(value: number): string {
  const hours = Math.floor(value / 3600);
  const minutes = Math.floor((value % 3600) / 60);
  const seconds = Math.round(value % 60);
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
    : `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function formatEvidenceValue(fact: ActivityEvidenceFact, locale: Locale): string {
  if (fact.value === null) return locale !== "zh-CN" ? "Not recorded" : "未记录";
  if (typeof fact.value === "boolean")
    return fact.value ? (locale !== "zh-CN" ? "Yes" : "是") : locale !== "zh-CN" ? "No" : "否";
  if (typeof fact.value === "string") return fact.value;
  if (fact.unit === "s") return formatSeconds(fact.value);
  if (fact.id === "metric.distance") return `${(fact.value / 1_000).toFixed(2)} km`;
  const digits = Math.abs(fact.value) >= 100 ? 0 : 1;
  return `${fact.value.toFixed(digits)}${fact.unit === null ? "" : ` ${fact.unit}`}`;
}

function evidenceLabel(fact: ActivityEvidenceFact, locale: Locale): string {
  const zhLabels: Record<string, string> = {
    "metric.distance": "有效路线距离",
    "metric.duration": "有效移动时间",
    "metric.speed": "平均速度",
    "metric.ascent": "累计爬升",
    "metric.heart_rate": "心率观测",
    "metric.cadence": "步频观测",
    "metric.power": "功率观测",
    "metric.temperature": "温度观测",
    "metric.calories": "设备能量估算",
    "terrain.grade_coverage": "坡度覆盖",
    "terrain.representative_steepness": "代表性陡度",
    "terrain.raw_maximum_grade": "原始最大坡度",
    "terrain.climb_count": "连续爬坡",
    "route.point_count": "空间观测",
    "route.point_density": "轨迹密度",
    "route.segment_count": "路线分段",
    "route.start_end_gap": "起终点距离",
    "route.is_loop": "闭环路线",
    "route.altitude_range": "海拔范围",
    "timing.elapsed_time": "总经过时间",
    "timing.pause_time": "非移动时间",
    "timing.moving_ratio": "移动占比",
    "source.sport": "来源运动类型",
  };
  return locale === "zh-CN" ? (zhLabels[fact.id] ?? fact.label) : fact.label;
}

function metricEvidenceReference(key: string): string {
  const ids: Record<string, string> = {
    heartRate: "heart_rate",
    movingTime: "duration",
    averageSpeed: "speed",
  };
  return `metric.${ids[key] ?? key.toLowerCase()}`;
}

function ActivityChat({
  activityId,
  locale,
  catalog,
  prompts,
  onSelectEvidence,
}: {
  activityId: string;
  locale: Locale;
  catalog: ActivityEvidenceCatalog | null;
  prompts: string[];
  onSelectEvidence: (reference: string) => void;
}) {
  const [messages, setMessages] = useState<
    Array<{
      role: "user" | "assistant";
      content: string;
      evidenceRefs?: string[];
      boundary?: string | null;
      suggestedQuestions?: string[];
    }>
  >([]);
  const [question, setQuestion] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const promptOptions =
    prompts.length > 0
      ? prompts
      : locale !== "zh-CN"
        ? ["What stands out?", "How demanding was the terrain?", "What data is missing?"]
        : ["这次活动最突出什么？", "地形强度如何？", "缺少了哪些数据？"];
  async function ask(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const content = question.trim();
    if (content === "" || busy) return;
    const next = [...messages, { role: "user" as const, content }].slice(-12);
    setMessages(next);
    setQuestion("");
    setBusy(true);
    setError("");
    try {
      const response = await api<ActivityChatResponse>(`/v1/activities/${activityId}/chat`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          locale,
          messages: next.map(({ role, content: message }) => ({ role, content: message })),
        }),
      });
      setMessages((current) => [
        ...current,
        {
          role: "assistant",
          content: response.value.answer,
          evidenceRefs: response.value.evidenceRefs,
          boundary: response.value.boundary,
          suggestedQuestions: response.value.suggestedQuestions,
        },
      ]);
    } catch (caught) {
      setError(errorText(locale, statusFromError(caught)));
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="report-chat" aria-labelledby="activity-chat-title">
      <div className="report-section-heading">
        <span>05</span>
        <div>
          <p>FOLLOW-UP ANALYST / {locale !== "zh-CN" ? "EVIDENCE-BOUND" : "证据限定"}</p>
          <h2 id="activity-chat-title">
            {locale !== "zh-CN" ? "Ask your activity" : "向你的活动提问"}
          </h2>
        </div>
      </div>
      <div className="chat-surface">
        {messages.length === 0 ? (
          <div className="chat-intro">
            <strong>
              {locale !== "zh-CN"
                ? "A conversation, grounded in this file."
                : "一场以该文件为依据的对话。"}
            </strong>
            <p>
              {locale !== "zh-CN"
                ? "The analyst can explain terrain, effort, sensor gaps, and confidence. It cannot see your exact route."
                : "分析员可解释地形、强度、传感器缺口与可信度，但看不到精确路线。"}
            </p>
            <div className="chat-prompts">
              {promptOptions.map((prompt) => (
                <button type="button" key={prompt} onClick={() => setQuestion(prompt)}>
                  {prompt}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="chat-thread" aria-live="polite">
            {messages.map((message, index) => (
              <article className={`chat-message ${message.role}`} key={`${message.role}-${index}`}>
                <span>
                  {message.role === "user" ? (locale !== "zh-CN" ? "YOU" : "你") : "AI Coach"}
                </span>
                <p>{message.content}</p>
                {message.evidenceRefs !== undefined && message.evidenceRefs.length > 0 && (
                  <div className="evidence-chips">
                    {message.evidenceRefs.map((reference) => {
                      const fact = evidenceFact(catalog, reference);
                      return (
                        <button
                          type="button"
                          key={reference}
                          onClick={() => onSelectEvidence(reference)}
                        >
                          {fact === null
                            ? reference
                            : `${evidenceLabel(fact, locale)} · ${formatEvidenceValue(fact, locale)}`}
                        </button>
                      );
                    })}
                  </div>
                )}
                {message.boundary !== undefined && message.boundary !== null && (
                  <small className="chat-boundary">{message.boundary}</small>
                )}
                {message.suggestedQuestions !== undefined &&
                  message.suggestedQuestions.length > 0 && (
                    <div className="chat-suggestions">
                      {message.suggestedQuestions.map((suggestion) => (
                        <button
                          type="button"
                          key={suggestion}
                          onClick={() => setQuestion(suggestion)}
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  )}
              </article>
            ))}
          </div>
        )}
        <form className="chat-composer" onSubmit={(event) => void ask(event)}>
          <label className="sr-only" htmlFor="activity-question">
            {locale !== "zh-CN" ? "Question about this activity" : "关于这次活动的问题"}
          </label>
          <textarea
            id="activity-question"
            value={question}
            maxLength={1200}
            rows={2}
            placeholder={locale !== "zh-CN" ? "Ask about the evidence…" : "询问这次活动的证据…"}
            onChange={(event) => setQuestion(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                event.currentTarget.form?.requestSubmit();
              }
            }}
          />
          <button type="submit" disabled={busy || question.trim() === ""}>
            {busy
              ? locale !== "zh-CN"
                ? "Thinking…"
                : "分析中…"
              : locale !== "zh-CN"
                ? "Ask →"
                : "提问 →"}
          </button>
        </form>
        <p className="chat-privacy">
          {locale !== "zh-CN"
            ? "Coordinate-free activity evidence is sent for each answer. SportTrack does not persist this transcript."
            : "每次回答仅发送无坐标活动证据；SportTrack 不保存此对话记录。"}
        </p>
        {error !== "" && (
          <p className="chat-error" role="alert">
            {error}
          </p>
        )}
      </div>
    </section>
  );
}

function ActivityView({ locale, activityId }: { locale: Locale; activityId: string }) {
  const [refresh, setRefresh] = useState(0);
  const detail = usePrivate<{
    activity: ActivitySummary & { notes: string; tags: string[]; qualityState: string };
    metrics: ActivityMetric[];
    evidence: ActivityAnalysis | null;
  }>(`/v1/activities/${activityId}`, refresh);
  const route = usePrivate<{
    segments: RoutePoint[][];
    version: number;
  }>(`/v1/activities/${activityId}/route`, refresh);
  const [story, setStory] = useState<ActivityStoryState | null>(null);
  const reducedMotion = useReducedMotion();
  const [selectedEvidenceRef, setSelectedEvidenceRef] = useState<string | null>(null);
  const [status, setStatus] = useState("");
  const [shareUrl, setShareUrl] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    let timer = 0;
    let requested = false;
    async function updateStory() {
      try {
        let response = await api<ActivityStoryState>(
          `/v1/activities/${activityId}/story?locale=${encodeURIComponent(locale)}`,
          { signal: controller.signal },
        );
        if (
          (response.value.state === "absent" || response.value.state === "failed") &&
          !requested
        ) {
          requested = true;
          response = await api<ActivityStoryState>(`/v1/activities/${activityId}/story`, {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ locale }),
            signal: controller.signal,
          });
        }
        setStory(response.value);
        if (
          ["queued", "generating"].includes(response.value.state) ||
          response.value.story?.image.state === "pending"
        )
          timer = window.setTimeout(() => void updateStory(), 1800);
      } catch (error) {
        if (!(error instanceof DOMException)) setStory(null);
      }
    }
    void updateStory();
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [activityId, locale]);

  if (detail.data === null) return <PanelState locale={locale} error={detail.error} />;
  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const values = new FormData(event.currentTarget);
    const tagsValue = values.get("tags");
    try {
      await api(`/v1/activities/${activityId}`, {
        method: "PATCH",
        headers: {
          "content-type": "application/json",
          "if-match": `"${detail.data!.activity.version}"`,
        },
        body: JSON.stringify({
          title: values.get("title"),
          notes: values.get("notes"),
          tags: (typeof tagsValue === "string" ? tagsValue : "")
            .split(",")
            .map((value) => value.trim())
            .filter(Boolean),
        }),
      });
      setStatus(
        locale !== "zh-CN" ? "Annotations saved with a new version." : "标注已保存为新版本。",
      );
      setRefresh((value) => value + 1);
    } catch (error) {
      setStatus(errorText(locale, statusFromError(error)));
    }
  }
  async function share(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const values = new FormData(event.currentTarget);
    const recipientValue = values.get("recipient");
    const email = (typeof recipientValue === "string" ? recipientValue : "").trim();
    try {
      const snapshot = await api<{ id: string }>(`/v1/activities/${activityId}/redaction-preview`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          mode: "mapless",
          trimStartMeters: 0,
          trimEndMeters: 0,
          trimStartSeconds: 0,
          trimEndSeconds: 0,
          zones: [],
          zoneIds: [],
          includedMetricKeys: ["distance", "duration", "ascent"],
          locale,
          units: "metric",
        }),
      });
      const created = await api<{ url: string }>("/v1/share-links", {
        method: "POST",
        headers: { "content-type": "application/json", "idempotency-key": crypto.randomUUID() },
        body: JSON.stringify({
          activityId,
          snapshotId: snapshot.value.id,
          mode: "mapless",
          ...(email === "" ? {} : { verificationEmail: email }),
        }),
      });
      setShareUrl(created.value.url);
      setStatus(
        locale !== "zh-CN"
          ? "Fixed share created. Later edits cannot change it."
          : "固定分享已创建，后续修改不会改变其内容。",
      );
    } catch (error) {
      setStatus(errorText(locale, statusFromError(error)));
    }
  }
  async function remove() {
    if (
      !confirm(
        locale !== "zh-CN"
          ? `Delete Activity ${activityId}? Shares are revoked immediately.`
          : `删除活动 ${activityId}？相关分享会立即撤销。`,
      )
    )
      return;
    try {
      await api(`/v1/activities/${activityId}`, {
        method: "DELETE",
        headers: { "content-type": "application/json", "idempotency-key": crypto.randomUUID() },
        body: JSON.stringify({ confirmation: activityId }),
      });
      location.assign("/app/activities");
    } catch (error) {
      setStatus(errorText(locale, statusFromError(error)));
    }
  }
  const activity = detail.data.activity;
  const metric = (key: string) => detail.data!.metrics.find((item) => item.key === key);
  const headlineMetrics = [
    metric("distance"),
    metric("duration") ?? metric("movingTime"),
    metric("ascent"),
    metric("speed") ?? metric("averageSpeed"),
  ];
  const evidence = detail.data.evidence;
  const storyNarrative = story?.story?.narrative ?? null;
  const evidenceCatalog = story?.story?.evidenceCatalog ?? null;
  const selectedEvidence =
    selectedEvidenceRef === null ? null : evidenceFact(evidenceCatalog, selectedEvidenceRef);
  const routePointCount =
    route.data?.segments.reduce((sum, segment) => sum + segment.length, 0) ?? 0;
  const qualityIssues =
    evidence === null
      ? 0
      : evidence.dataQuality.spatial.length +
        evidence.dataQuality.temporal.length +
        evidence.dataQuality.elevation.length +
        evidence.dataQuality.sensors.length;
  return (
    <article className="activity-report">
      <section className="report-hero procedural">
        <header className="report-hero-copy">
          <div className="report-kicker">
            <span>{activity.sport.toUpperCase()}</span>
            <span>{activity.localDate}</span>
            <span>{activity.qualityState.toUpperCase()}</span>
          </div>
          <h1>{activity.title}</h1>
          <p className="report-deck">
            {storyNarrative?.headline ??
              evidence?.verdict ??
              (locale !== "zh-CN"
                ? "A measured view of the evidence in this activity."
                : "以审慎的方式看待这次活动中的证据。")}
          </p>
          <div className="report-model-state" aria-live="polite">
            <span className={story?.state === "ready" ? "ready" : ""} />
            {story?.state === "ready"
              ? locale !== "zh-CN"
                ? "SOL ANALYSIS READY"
                : "SOL 分析已就绪"
              : story?.state === "failed"
                ? locale !== "zh-CN"
                  ? "CANONICAL ANALYSIS · AI STORY UNAVAILABLE"
                  : "规范分析 · AI 故事暂不可用"
                : locale !== "zh-CN"
                  ? "CANONICAL ANALYSIS · BUILDING AI STORY"
                  : "规范分析 · 正在生成 AI 故事"}
          </div>
        </header>
        <div className="report-metric-strip">
          {headlineMetrics.map((item, index) => (
            <div key={item?.key ?? `missing-${index}`}>
              <small>{item === undefined ? "—" : metricLabel(item.key, locale)}</small>
              <strong>{formatMetric(item, locale)}</strong>
              <span>
                {item === undefined
                  ? "missing"
                  : `${item.state} · ${Math.round(item.coverage * 100)}%`}
              </span>
            </div>
          ))}
        </div>
      </section>

      <section
        className={`report-relief${story?.imageUrl ? " generated-atmosphere" : ""}`}
        style={
          story?.imageUrl
            ? {
                backgroundImage: `linear-gradient(rgba(5, 9, 7, .78), rgba(5, 9, 7, .9)), url(${story.imageUrl})`,
              }
            : undefined
        }
      >
        <div className="report-section-heading inverse">
          <span>01</span>
          <div>
            <p>LOCAL ROUTE ENGINE / THREE.JS</p>
            <h2>{locale !== "zh-CN" ? "The shape of the effort" : "一次运动的地形形状"}</h2>
          </div>
          <small>
            {routePointCount} {locale !== "zh-CN" ? "spatial observations" : "个空间观测"}
          </small>
        </div>
        {route.data === null ? (
          <PanelState locale={locale} error={route.error} />
        ) : (
          <Suspense
            fallback={
              <div className="terrain-empty" aria-busy="true">
                <span>{locale !== "zh-CN" ? "BUILDING LOCAL RELIEF" : "正在构建本地地形"}</span>
              </div>
            }
          >
            <ActivityTerrain
              locale={locale}
              segments={route.data.segments}
              reducedMotion={reducedMotion}
              selectedRange={selectedEvidence?.routeRange ?? null}
              label={
                locale !== "zh-CN"
                  ? `Interactive relief of ${activity.title}; ${route.data.segments.length} segments and ${routePointCount} observations. Gaps remain disconnected.`
                  : `${activity.title} 的交互地形，共 ${route.data.segments.length} 个分段与 ${routePointCount} 个观测，分段间隙保持断开。`
              }
            />
          </Suspense>
        )}
        {selectedEvidence !== null && (
          <button
            className="terrain-active-evidence"
            type="button"
            onClick={() => setSelectedEvidenceRef(null)}
          >
            <span>{locale !== "zh-CN" ? "ACTIVE EVIDENCE" : "当前证据"}</span>
            <strong>{evidenceLabel(selectedEvidence, locale)}</strong>
            <small>{formatEvidenceValue(selectedEvidence, locale)} · ×</small>
          </button>
        )}
        <div
          className="grade-legend"
          aria-label={locale !== "zh-CN" ? "Grade color legend" : "坡度颜色图例"}
        >
          <span>
            <i className="descent" />
            {locale !== "zh-CN" ? "Descent" : "下坡"}
          </span>
          <span>
            <i className="level" />
            {locale !== "zh-CN" ? "Level" : "平缓"}
          </span>
          <span>
            <i className="climb" />
            {locale !== "zh-CN" ? "Climb" : "爬坡"}
          </span>
          <span>
            <i className="steep" />
            {locale !== "zh-CN" ? "Steep" : "陡坡"}
          </span>
        </div>
        <p className="route-boundary">
          {locale !== "zh-CN"
            ? "Rendered locally from the private route. Elevation relief is visually exaggerated for legibility; distance, grade colors, and segment gaps remain canonical. Exact coordinates never enter the text or image model."
            : "私密路线仅在本地渲染。为便于阅读，海拔起伏经过视觉放大；距离、坡度颜色与分段间隙仍以规范数据为准。精确坐标不会发送给文本或图像模型。"}
        </p>
      </section>

      <section className="report-analysis-grid">
        <div className="analysis-lead">
          <div className="report-section-heading">
            <span>02</span>
            <div>
              <p>
                {locale !== "zh-CN" ? "ACTIVITY STORY / EVIDENCE FIRST" : "活动故事 / 证据先行"}
              </p>
              <h2>{locale !== "zh-CN" ? "What the evidence changes" : "证据真正改变了什么"}</h2>
            </div>
          </div>
          <p className="analysis-synopsis">
            {storyNarrative?.overview ??
              evidence?.recommendation ??
              (locale !== "zh-CN"
                ? "The canonical report is ready. A richer model-written interpretation is being generated in the background."
                : "规范报告已就绪，更深入的模型解读正在后台生成。")}
          </p>
          <div className="story-findings">
            {(storyNarrative?.sections ?? []).map((section, index) => (
              <article key={`${section.title}-${index}`}>
                <span>
                  {String(index + 1).padStart(2, "0")} / {section.category.replaceAll("_", " ")}
                </span>
                <h3>{section.title}</h3>
                <p>{section.body}</p>
                <ul className="analysis-findings-list">
                  {section.findings.map((finding) => (
                    <li className={finding.kind} key={`${finding.kind}-${finding.text}`}>
                      {finding.text}
                    </li>
                  ))}
                </ul>
                <div className="evidence-chips">
                  <small>{section.confidence.toUpperCase()} CONFIDENCE</small>
                  {section.evidenceRefs.map((reference) => {
                    const fact = evidenceFact(evidenceCatalog, reference);
                    return (
                      <button
                        className={selectedEvidenceRef === reference ? "selected" : ""}
                        type="button"
                        key={reference}
                        onClick={() => setSelectedEvidenceRef(reference)}
                      >
                        {fact === null
                          ? reference
                          : `${evidenceLabel(fact, locale)} · ${formatEvidenceValue(fact, locale)}`}
                      </button>
                    );
                  })}
                </div>
              </article>
            ))}
          </div>
        </div>

        <aside className="analysis-rail">
          <div className="rail-block">
            <p>TERRAIN / {Math.round((evidence?.grade.coverage ?? 0) * 100)}% COVERAGE</p>
            <div className="grade-bar">
              {(evidence?.grade.bands ?? []).map((band) => (
                <span
                  className={band.key}
                  key={band.key}
                  style={{ width: `${Math.max(band.share * 100, 0.5)}%` }}
                />
              ))}
            </div>
            <div className="band-list">
              {(evidence?.grade.bands ?? []).map((band) => (
                <div key={band.key}>
                  <span>
                    <i className={band.key} />
                    {band.key}
                  </span>
                  <strong>{Math.round(band.share * 100)}%</strong>
                  <small>{(band.distance / 1000).toFixed(2)} km</small>
                </div>
              ))}
            </div>
          </div>
          <div className="rail-block rail-numbers">
            <div>
              <small>{locale !== "zh-CN" ? "Representative steepness" : "代表性陡度"}</small>
              <strong>
                {evidence?.grade.representativeSteepness === null ||
                evidence?.grade.representativeSteepness === undefined
                  ? "—"
                  : `${evidence.grade.representativeSteepness.toFixed(1)}%`}
              </strong>
            </div>
            <div>
              <small>{locale !== "zh-CN" ? "Detected climbs" : "检测到爬坡"}</small>
              <strong>{evidence?.climbs.length ?? 0}</strong>
            </div>
            <div>
              <small>{locale !== "zh-CN" ? "Quality signals" : "质量信号"}</small>
              <strong>{qualityIssues}</strong>
            </div>
          </div>
          {evidence?.effort !== null && evidence?.effort !== undefined && (
            <div className="rail-block">
              <p>EFFORT RHYTHM / {Math.round(evidence.effort.coverage * 100)}% COVERAGE</p>
              <div className="effort-bands">
                {evidence.effort.bands.map((band) => (
                  <div key={band.key}>
                    <span>{band.key}</span>
                    <strong>{Math.round(band.share * 100)}%</strong>
                    <i style={{ width: `${band.share * 100}%` }} />
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="rail-block rail-recommendations">
            <p>
              NEXT OUTING / {storyNarrative?.recommendations.length ?? 1}{" "}
              {locale !== "zh-CN" ? "ACTIONS" : "条建议"}
            </p>
            {(storyNarrative?.recommendations ?? []).map((recommendation, index) => (
              <article key={`${recommendation.title}-${index}`}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <div>
                  <small>{recommendation.category.replaceAll("_", " ")}</small>
                  <h3>{recommendation.title}</h3>
                  <p>{recommendation.body}</p>
                  <ol>
                    {recommendation.steps.map((step) => (
                      <li key={step}>{step}</li>
                    ))}
                  </ol>
                  <div className="evidence-chips">
                    {recommendation.evidenceRefs.map((reference) => {
                      const fact = evidenceFact(evidenceCatalog, reference);
                      return (
                        <button
                          className={selectedEvidenceRef === reference ? "selected" : ""}
                          type="button"
                          key={reference}
                          onClick={() => setSelectedEvidenceRef(reference)}
                        >
                          {fact === null
                            ? reference
                            : `${evidenceLabel(fact, locale)} · ${formatEvidenceValue(fact, locale)}`}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </article>
            ))}
            {storyNarrative === null && (
              <article>
                <span>01</span>
                <div>
                  <h3>
                    {locale !== "zh-CN"
                      ? "Keep the evidence boundary visible"
                      : "让证据边界保持可见"}
                  </h3>
                  <p>
                    {evidence?.recommendation ??
                      (locale !== "zh-CN"
                        ? "Review metric coverage before comparing this activity."
                        : "比较活动前，请先检查指标覆盖率。")}
                  </p>
                </div>
              </article>
            )}
          </div>
        </aside>
      </section>

      <section className="report-evidence-ledger">
        <div className="report-section-heading">
          <span>04</span>
          <div>
            <p>CANONICAL LEDGER / MISSING ≠ ZERO</p>
            <h2>
              {locale !== "zh-CN" ? "Every metric, with its receipts" : "每个指标，都有证据链"}
            </h2>
          </div>
        </div>
        <div className="ledger-table">
          {detail.data.metrics.map((item) => {
            const reference = metricEvidenceReference(item.key);
            return (
              <details
                className={selectedEvidenceRef === reference ? "selected" : ""}
                data-evidence-ref={reference}
                key={item.key}
              >
                <summary>
                  <span>{metricLabel(item.key, locale)}</span>
                  <strong>{formatMetric(item, locale)}</strong>
                  <i
                    style={{ "--coverage": `${Math.round(item.coverage * 100)}%` } as CSSProperties}
                  />
                  <small>
                    {item.state} · {Math.round(item.coverage * 100)}% · {item.confidence}
                  </small>
                </summary>
                <p>{item.provenance}</p>
                <p>
                  {item.warnings.join(" · ") ||
                    (locale !== "zh-CN" ? "No declared limitation." : "无已声明限制。")}
                </p>
              </details>
            );
          })}
        </div>
        {storyNarrative !== null && (
          <div className="limitations-list">
            <strong>{locale !== "zh-CN" ? "Model-visible limitations" : "模型可见的限制"}</strong>
            {storyNarrative.limitations.map((limitation) => (
              <span key={limitation}>{limitation}</span>
            ))}
          </div>
        )}
      </section>

      <ActivityChat
        activityId={activityId}
        locale={locale}
        catalog={evidenceCatalog}
        prompts={storyNarrative?.suggestedQuestions ?? []}
        onSelectEvidence={setSelectedEvidenceRef}
      />

      <section className="report-tools">
        <details>
          <summary>{locale !== "zh-CN" ? "Edit annotations" : "编辑标注"}</summary>
          <form onSubmit={(event) => void save(event)}>
            <h2>{locale !== "zh-CN" ? "Annotations" : "标注"}</h2>
            <label>
              <span>{locale !== "zh-CN" ? "Title" : "标题"}</span>
              <input name="title" defaultValue={activity.title} required />
            </label>
            <label>
              <span>{locale !== "zh-CN" ? "Notes" : "备注"}</span>
              <textarea name="notes" defaultValue={activity.notes} />
            </label>
            <label>
              <span>{locale !== "zh-CN" ? "Tags, comma separated" : "标签（逗号分隔）"}</span>
              <input name="tags" defaultValue={activity.tags.join(", ")} />
            </label>
            <button className="button">
              {locale !== "zh-CN" ? "Save new version" : "保存新版本"}
            </button>
          </form>
        </details>
        <details>
          <summary>{locale !== "zh-CN" ? "Share or delete" : "分享或删除"}</summary>
          <form onSubmit={(event) => void share(event)}>
            <h2>{words[locale].createShare}</h2>
            <p>
              {locale !== "zh-CN"
                ? "Mapless is selected first. Optionally require the intended recipient email."
                : "默认选中无地图分享；如需，可要求填写接收者邮箱进行验证。"}
            </p>
            <label>
              <span>
                {locale !== "zh-CN" ? "Recipient email (optional)" : "接收者邮箱（可选）"}
              </span>
              <input name="recipient" type="email" />
            </label>
            <button className="button">{words[locale].createShare}</button>
            {shareUrl !== "" && (
              <a className="quiet-link" href={shareUrl}>
                {shareUrl}
              </a>
            )}
            <button className="danger-button" type="button" onClick={() => void remove()}>
              {words[locale].deleteActivity}
            </button>
          </form>
        </details>
      </section>
      <p role="status">{status}</p>
    </article>
  );
}

function CompareView({ locale }: { locale: Locale }) {
  const activities = usePrivate<{ activities: ActivitySummary[] }>("/v1/activities");
  const [result, setResult] = useState<null | {
    groups: {
      sport: string;
      sampleSize: number;
      totalDistance: number;
      aggregateSpeed: number | null;
      limitations: string[];
    }[];
  }>(null);
  const [status, setStatus] = useState("");
  async function compare(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const ids = new FormData(event.currentTarget).getAll("activity").map(String);
    if (ids.length === 0) return;
    try {
      const response = await api<typeof result extends null ? never : NonNullable<typeof result>>(
        "/v1/comparisons",
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ activityIds: ids }),
        },
      );
      setResult(response.value);
    } catch (error) {
      setStatus(errorText(locale, statusFromError(error)));
    }
  }
  if (activities.data === null) return <PanelState locale={locale} error={activities.error} />;
  return (
    <>
      <header className="app-title">
        <div>
          <p className="eyebrow">
            {locale !== "zh-CN" ? "SAME SPORT / ELIGIBLE EVIDENCE" : "同运动 / 符合条件的证据"}
          </p>
          <h1>{words[locale].compare}</h1>
        </div>
      </header>
      <form className="private-card selection-list" onSubmit={(event) => void compare(event)}>
        {activities.data.activities.map((activity) => (
          <label key={activity.id}>
            <input type="checkbox" name="activity" value={activity.id} />
            <span>
              {activity.title} · {activity.sport} · {activity.localDate}
            </span>
          </label>
        ))}
        <button className="button">
          {locale !== "zh-CN" ? "Compare selected evidence" : "比较所选证据"}
        </button>
      </form>
      {result !== null && (
        <section className="metric-cards">
          {result.groups.map((group) => (
            <article className="private-card" key={group.sport}>
              <h2>{group.sport}</h2>
              <strong>{group.sampleSize}</strong>
              <p>
                {(group.totalDistance / 1000).toFixed(1)} km ·{" "}
                {group.aggregateSpeed === null ? "N/A" : `${group.aggregateSpeed.toFixed(2)} m/s`}
              </p>
              <p>{group.limitations.join(" · ")}</p>
            </article>
          ))}
        </section>
      )}
      <p role="status">{status}</p>
    </>
  );
}

function ReportsView({ locale }: { locale: Locale }) {
  const [report, setReport] = useState<null | { id: string; state: string; pdfReady: boolean }>(
    null,
  );
  const [status, setStatus] = useState("");
  async function create(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const values = new FormData(event.currentTarget);
    try {
      const response = await api<{ id: string; state: string; pdfReady: boolean }>("/v1/reports", {
        method: "POST",
        headers: { "content-type": "application/json", "idempotency-key": crypto.randomUUID() },
        body: JSON.stringify({
          start: values.get("start"),
          end: values.get("end"),
          sports: ["cycling", "running", "hiking"],
          tags: [],
          locale,
          units: "metric",
          privacyMode: "mapless",
          sections: [
            "summary",
            "volume",
            "terrain",
            "sensors",
            "records",
            "privacy",
            "limitations",
          ],
        }),
      });
      setReport(response.value);
      setStatus(
        locale !== "zh-CN"
          ? "Report snapshot pinned; PDF rendering is queued."
          : "报告快照已固定，PDF 正在排队渲染。",
      );
    } catch (error) {
      setStatus(errorText(locale, statusFromError(error)));
    }
  }
  const today = new Date().toISOString().slice(0, 10);
  const start = `${today.slice(0, 4)}-01-01`;
  return (
    <>
      <header className="app-title">
        <div>
          <p className="eyebrow">
            {locale !== "zh-CN" ? "PINNED INPUTS / DETERMINISTIC" : "固定输入 / 结果确定"}
          </p>
          <h1>{words[locale].reports}</h1>
        </div>
      </header>
      <form className="private-card report-form" onSubmit={(event) => void create(event)}>
        <label>
          <span>{locale !== "zh-CN" ? "Start" : "开始"}</span>
          <input name="start" type="date" defaultValue={start} required />
        </label>
        <label>
          <span>{locale !== "zh-CN" ? "End" : "结束"}</span>
          <input name="end" type="date" defaultValue={today} required />
        </label>
        <button className="button">
          {locale !== "zh-CN" ? "Pin report and queue PDF" : "固定报告并排队生成 PDF"}
        </button>
      </form>
      {report !== null && (
        <section className="private-card">
          <h2>{report.id}</h2>
          <p>
            {report.state} · PDF {report.pdfReady ? "ready" : "queued"}
          </p>
          {report.pdfReady && (
            <a className="button secondary" href={`/v1/reports/${report.id}/pdf`}>
              PDF
            </a>
          )}
        </section>
      )}
      <p role="status">{status}</p>
    </>
  );
}

function SettingsView({ locale }: { locale: Locale }) {
  const account = usePrivate<{
    preferences: {
      locale: Locale;
      units: "metric" | "imperial";
      timezone: string;
      weekStart: "monday" | "sunday";
      theme: "system" | "dark" | "light";
      version: number;
    } | null;
  }>("/v1/account");
  const privacy = usePrivate<{
    purposes: { purpose: string; decision: unknown }[];
    retention: unknown;
    preferences: { deleteOriginalAfterAnalysis: number; version: number } | null;
    entitlements: { usage: Record<string, number>; limits: Record<string, unknown> };
  }>("/v1/privacy-center");
  const sessions = usePrivate<{ sessions: { id: string; createdAt: string; current: boolean }[] }>(
    "/v1/security/sessions",
  );
  const [status, setStatus] = useState("");
  async function savePreferences(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const current = account.data?.preferences;
    if (current === null || current === undefined) return;
    const values = new FormData(event.currentTarget);
    try {
      const selectedLocale = values.get("locale");
      const nextLocale: Locale = isLocale(selectedLocale) ? selectedLocale : "en";
      await api("/v1/preferences", {
        method: "PUT",
        headers: { "content-type": "application/json", "if-match": `"${current.version}"` },
        body: JSON.stringify({
          locale: nextLocale,
          units: values.get("units"),
          timezone: values.get("timezone"),
          weekStart: values.get("weekStart"),
          theme: values.get("theme"),
        }),
      });
      localStorage.setItem("sporttrack-locale", nextLocale);
      location.reload();
    } catch (error) {
      setStatus(errorText(locale, statusFromError(error)));
    }
  }
  async function saveRetention(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const current = privacy.data?.preferences;
    if (current === null || current === undefined) return;
    const values = new FormData(event.currentTarget);
    try {
      await api("/v1/retention-preference", {
        method: "PUT",
        headers: { "content-type": "application/json", "if-match": `"${current.version}"` },
        body: JSON.stringify({
          deleteOriginalAfterAnalysis: values.get("deleteOriginal") === "on",
        }),
      });
      location.reload();
    } catch (error) {
      setStatus(errorText(locale, statusFromError(error)));
    }
  }
  async function createExport() {
    try {
      const result = await api<{ id: string; state: string }>("/v1/account-export", {
        method: "POST",
        headers: { "content-type": "application/json", "idempotency-key": crypto.randomUUID() },
        body: "{}",
      });
      setStatus(`${words[locale].export}: ${result.value.state} · ${result.value.id}`);
    } catch (error) {
      setStatus(errorText(locale, statusFromError(error)));
    }
  }
  async function revoke() {
    try {
      const response = await fetch("/v1/security/sessions", {
        method: "DELETE",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: "{}",
      });
      if (!response.ok) throw new Error(String(response.status));
      location.assign("/app");
    } catch (error) {
      setStatus(errorText(locale, statusFromError(error)));
    }
  }
  return (
    <>
      <header className="app-title">
        <div>
          <p className="eyebrow">
            {locale !== "zh-CN" ? "RIGHTS / OWNER CONTROL" : "权益 / 本人控制"}
          </p>
          <h1>{words[locale].settings}</h1>
        </div>
      </header>
      <div className="private-layout">
        <section className="private-card">
          <h2>
            {locale !== "zh-CN" ? "Display and local-time preferences" : "显示和本地时间偏好"}
          </h2>
          {account.data?.preferences === null || account.data === null ? (
            <PanelState locale={locale} error={account.error} />
          ) : (
            <form onSubmit={(event) => void savePreferences(event)}>
              <label>
                <span>{messages[locale].language}</span>
                <select name="locale" defaultValue={account.data.preferences.locale}>
                  {LOCALES.map((item) => (
                    <option key={item.code} value={item.code}>
                      {item.nativeName}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>{locale !== "zh-CN" ? "Units" : "单位"}</span>
                <select name="units" defaultValue={account.data.preferences.units}>
                  <option value="metric">{locale !== "zh-CN" ? "Metric" : "公制"}</option>
                  <option value="imperial">{locale !== "zh-CN" ? "Imperial" : "英制"}</option>
                </select>
              </label>
              <label>
                <span>{locale !== "zh-CN" ? "IANA timezone" : "IANA 时区"}</span>
                <input name="timezone" defaultValue={account.data.preferences.timezone} required />
              </label>
              <label>
                <span>{locale !== "zh-CN" ? "Week starts" : "每周起始日"}</span>
                <select name="weekStart" defaultValue={account.data.preferences.weekStart}>
                  <option value="monday">{locale !== "zh-CN" ? "Monday" : "星期一"}</option>
                  <option value="sunday">{locale !== "zh-CN" ? "Sunday" : "星期日"}</option>
                </select>
              </label>
              <label>
                <span>{locale !== "zh-CN" ? "Appearance" : "外观"}</span>
                <select name="theme" defaultValue={account.data.preferences.theme}>
                  <option value="system">{locale !== "zh-CN" ? "System" : "跟随系统"}</option>
                  <option value="dark">{locale !== "zh-CN" ? "Dark" : "深色"}</option>
                  <option value="light">{locale !== "zh-CN" ? "Light" : "浅色"}</option>
                </select>
              </label>
              <button className="button">
                {locale !== "zh-CN" ? "Save preferences" : "保存偏好"}
              </button>
            </form>
          )}
        </section>
        <section className="private-card">
          <h2>{locale !== "zh-CN" ? "Purposes, retention, and limits" : "处理目的、保留和限制"}</h2>
          {privacy.data === null ? (
            <PanelState locale={locale} error={privacy.error} />
          ) : (
            <>
              <ul className="evidence-list">
                {privacy.data.purposes.map((purpose) => (
                  <li key={purpose.purpose}>
                    <strong>{purpose.purpose}</strong>
                    <span>
                      {purpose.decision === null
                        ? locale !== "zh-CN"
                          ? "not decided"
                          : "未决定"
                        : locale !== "zh-CN"
                          ? "versioned decision recorded"
                          : "已记录版本化决定"}
                    </span>
                  </li>
                ))}
              </ul>
              <pre className="safe-json">{JSON.stringify(privacy.data.entitlements, null, 2)}</pre>
              {privacy.data.preferences !== null && (
                <form onSubmit={(event) => void saveRetention(event)}>
                  <label className="check">
                    <input
                      name="deleteOriginal"
                      type="checkbox"
                      defaultChecked={privacy.data.preferences.deleteOriginalAfterAnalysis === 1}
                    />
                    <span>
                      {locale !== "zh-CN"
                        ? "Delete future original files after canonical analysis"
                        : "规范分析后删除今后上传的原文件"}
                    </span>
                  </label>
                  <button className="button secondary">
                    {locale !== "zh-CN" ? "Save source retention" : "保存源文件保留设置"}
                  </button>
                </form>
              )}
            </>
          )}
        </section>
        <section className="private-card">
          <h2>{locale !== "zh-CN" ? "Sessions and portability" : "会话和可移植性"}</h2>
          {sessions.data === null ? (
            <PanelState locale={locale} error={sessions.error} />
          ) : (
            <p>
              {sessions.data.sessions.length}{" "}
              {locale !== "zh-CN" ? "active session(s)" : "个活跃会话"}
            </p>
          )}
          <button className="button secondary" onClick={() => void createExport()}>
            {words[locale].export}
          </button>
          <button className="danger-button" onClick={() => void revoke()}>
            {words[locale].revokeSessions}
          </button>
        </section>
      </div>
      <p role="status">{status}</p>
    </>
  );
}

export function PrivateWorkspace({
  locale,
  section,
  path,
  studio,
}: {
  locale: Locale;
  section: string;
  path: string;
  studio: ReactNode;
}) {
  const activityId = path.match(/^\/app\/activities\/([^/]+)$/u)?.[1];
  if (activityId !== undefined)
    return <ActivityView locale={locale} activityId={decodeURIComponent(activityId)} />;
  if (section === "activities") return <ActivitiesView locale={locale} />;
  if (section === "compare") return <CompareView locale={locale} />;
  if (section === "reports") return <ReportsView locale={locale} />;
  if (section === "settings") return <SettingsView locale={locale} />;
  if (section === "studio") return studio;
  return <HomeView locale={locale} />;
}
